/**
 * React 19 native metadata. <title>, <meta>, <link>, and <script> rendered
 * inside JSX get hoisted into <head> automatically — no react-helmet needed.
 *
 * `JsonLd` wraps a structured-data <script> with a stable `key` so multiple
 * Seo blocks on the same page don't collide.
 */

import React from 'react';

interface SeoProps {
  title: string;
  description: string;
  /** Path including leading slash, e.g. "/blog". Combined with the canonical origin. */
  path: string;
  ogImage?: string;
  /** Defaults to `index, follow`. Set to `noindex` for admin/editor pages. */
  robots?: string;
  /** "article" for blog posts, otherwise "website". */
  ogType?: 'website' | 'article';
  /** ISO date — emitted as article:published_time when ogType="article" (richer Pinterest/OG Article pins). */
  publishedTime?: string;
  /** Author name — emitted as article:author when ogType="article". */
  author?: string;
}

const ORIGIN = 'https://brandctl.com';
// Default Open Graph image — 1200×630 PNG rasterized from og-image.svg
// (the SVG stays the source of truth; regenerate via @resvg/resvg-js per
// CLAUDE.md → "OG image regeneration"). PNG renders on every platform
// including LinkedIn + Facebook, which don't rasterize SVG.
const DEFAULT_OG = 'https://brandctl.com/og-image.png';

export const Seo = ({
  title,
  description,
  path,
  ogImage = DEFAULT_OG,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1',
  ogType = 'website',
  publishedTime,
  author,
}: SeoProps) => {
  const canonical = `${ORIGIN}${path}`;
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      {/* Article-specific OG tags — power richer Pinterest Article Rich
          Pins + LinkedIn/FB article previews. Only emitted for ogType
          "article" (blog posts). */}
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {/* og:site_name is what Google reads to render the site name
          under the page title in SERPs (instead of falling back to
          the bare domain "brandctl.com"). Pair this with the WebSite
          JSON-LD `name` field in index.html — both must agree on
          "BRANDCENTRAL" for Google to lock in the site-name display. */}
      <meta property="og:site_name" content="BRANDCENTRAL" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@SamKern1234BC" />
    </>
  );
};

export const JsonLd = ({
  id,
  data,
}: {
  /** Unique key so multiple JSON-LD scripts on a page don't collide. */
  id: string;
  data: Record<string, unknown>;
}) => (
  <script
    key={id}
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
);
